import { getConfig } from "../../config.js";
import apiClient from "@/utils/httpClient";

function jsonHeaders(): Record<string, string> {
  const organizationId = apiClient.getOrganizationId();
  const viewId = apiClient.getViewId();
  return {
    'Content-Type': 'application/json',
    ...(organizationId && { 'x-organization-id': organizationId }),
    ...(viewId && { 'x-view-id': viewId }),
  };
}

export type FeedbackType = 'like' | 'dislike';
export type DislikeReason = 'incorrect' | 'too_generic' | 'not_actionable' | 'too_long' | 'other';

export interface SubmitFeedbackOptions {
  threadId: string;
  question: string;
  answer: string;
  feedback: FeedbackType;
  dislikeReason?: DislikeReason;
  customReason?: string;
}

export interface RegenerateAnswerOptions {
  threadId: string;
  question: string;
  answer: string;
  dislikeReason?: DislikeReason;
  customReason?: string;
  onChunk: (content: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export interface ChatStreamChunk {
  content: string;
}

export interface ChatStreamOptions {
  threadId: string;
  message: string;
  onChunk: (content: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export interface Conversation {
  id: string;
  title: string;
  resourceId: string;
  lastMessageTime: Date;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string | { content?: string; parts?: Array<{ text?: string }> }; // Content can be string or object with format/parts/content
}

/**
 * Sends a chat message to the AI agent and streams the response
 * @param options - Chat stream configuration options
 */
export async function streamChatMessage(options: ChatStreamOptions): Promise<void> {
  const { threadId, message, onChunk, onComplete, onError } = options;

  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders(),
      body: JSON.stringify({
        threadId,
        message,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Split by double newlines to get complete SSE messages
      const lines = buffer.split('\n\n');

      // Keep the last incomplete part in the buffer
      buffer = lines.pop() || '';

      // Process each complete SSE message
      for (const line of lines) {
        if (!line.trim()) continue;

        // Check if this is the end marker
        if (line.includes('data: [DONE]')) {
          onComplete();
          return;
        }

        // Parse SSE format: "data: {json}"
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6); // Remove "data: " prefix
          try {
            const data = JSON.parse(jsonStr) as ChatStreamChunk;
            if (data.content) {
              onChunk(data.content);
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

    onComplete();
  } catch (error) {
    console.error('Error streaming chat message:', error);
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
}

export interface ConversationsResponse {
  conversations: Conversation[];
  totalCount: number;
  currentPage: number;
}

/**
 * Gets the list of conversation threads
 * @param page - Page number (default: 1)
 * @returns Conversations response with pagination info
 */
export async function getConversations(page: number = 1): Promise<ConversationsResponse> {
  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations?page=${page}`, {
      method: 'GET',
      credentials: 'include',
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ConversationsResponse = await response.json();
    return {
      conversations: data.conversations || [],
      totalCount: data.totalCount || 0,
      currentPage: data.currentPage || page,
    };
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
}

/**
 * Gets the chat history for a specific thread
 * @param threadId - The thread ID to fetch history for
 * @returns Array of chat messages
 */
export async function getChatHistory(threadId: string): Promise<ChatMessage[]> {
  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}/history`, {
      method: 'GET',
      credentials: 'include',
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('Error fetching chat history:', error);
    throw error;
  }
}

/**
 * Sets the title for a conversation thread (triggers title generation on backend)
 * @param threadId - The thread ID to set title for
 * @returns Promise that resolves when title is set
 */
export async function setConversationTitle(threadId: string): Promise<void> {
  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}/title`, {
      method: 'GET',
      credentials: 'include',
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error setting conversation title:', error);
    throw error;
  }
}

/**
 * Aborts an ongoing chat stream for a conversation thread
 * @param threadId - The thread ID to abort
 * @returns Promise that resolves when the stream is aborted
 */
export async function abortChat(threadId: string): Promise<void> {
  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}/abort`, {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error aborting chat:', error);
    throw error;
  }
}

/**
 * Submits like/dislike feedback for an AI response
 */
export async function submitFeedback(options: SubmitFeedbackOptions): Promise<void> {
  const { threadId, ...body } = options;
  const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
}

/**
 * Submits dislike feedback and streams an improved response from the agent
 */
export async function regenerateAnswer(options: RegenerateAnswerOptions): Promise<void> {
  const { threadId, onChunk, onComplete, onError, ...body } = options;

  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}/feedback`, {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders(),
      body: JSON.stringify({ ...body, feedback: 'dislike', regenerate: true }),
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;
        if (line.includes('data: [DONE]')) { onComplete(); return; }
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6)) as ChatStreamChunk;
            if (data.content) onChunk(data.content);
          } catch { /* ignore parse errors */ }
        }
      }
    }
    onComplete();
  } catch (error) {
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
}

/**
 * Deletes a conversation thread
 * @param threadId - The thread ID to delete
 * @returns Promise that resolves when the conversation is deleted
 */
export async function deleteConversation(threadId: string): Promise<void> {
  try {
    const response = await fetch(`${getConfig().get('api.baseUrl')}/agent/conversations/${threadId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
}
